const MONEY_TOKENS = {
  amount: [2, ',', '.'],
  amount_no_decimals: [0, ',', '.'],
  amount_with_comma_separator: [2, '.', ','],
  amount_no_decimals_with_comma_separator: [0, '.', ','],
  amount_with_apostrophe_separator: [2, "'", '.'],
  amount_no_decimals_with_space_separator: [0, ' ', '.'],
  amount_with_space_separator: [2, ' ', ','],
  amount_with_period_and_space_separator: [2, ' ', '.'],
};

/**
 * @param {ParentNode} root
 */
function initBundle(root) {
  if (!(root instanceof HTMLElement) || root.dataset.byobReady === 'true') return;
  root.dataset.byobReady = 'true';

  const cards = [...root.querySelectorAll('[data-byob-card]')];
  if (!cards.length) return;

  /** @type {Map<string, {lines: Map<number, number>, variants: Array<Record<string, any>>, card: HTMLElement, title: string}>} */
  const state = new Map();

  const countEl = root.querySelector('[data-byob-count]');
  const fillEl = root.querySelector('[data-byob-progress-fill]');
  const moreEl = root.querySelector('[data-byob-more]');
  const thumbsEl = root.querySelector('[data-byob-thumbs]');
  const mrpEl = root.querySelector('[data-byob-mrp]');
  const finalEl = root.querySelector('[data-byob-final]');
  const saveEl = root.querySelector('[data-byob-save]');
  const atcButton = root.querySelector('[data-byob-atc]');
  const errorEl = root.querySelector('[data-byob-error]');
  const slots = [...root.querySelectorAll('[data-byob-slot]')];

  cards.forEach((card) => {
    const script = card.querySelector('[data-byob-variants]');
    let variants = [];
    try {
      variants = JSON.parse(script?.textContent || '[]');
    } catch {
      variants = [];
    }
    if (!Array.isArray(variants) || !variants.length) return;

    ensureSelection(card, variants);
    state.set(card.dataset.blockId || card.id, {
      lines: new Map(),
      variants,
      card,
      title: card.dataset.productTitle || '',
    });
    syncCard(card, false);
  });

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('[data-byob-card]');
    const pill = target.closest('[data-byob-pill]');
    if (pill instanceof HTMLButtonElement && card instanceof HTMLElement) {
      if (pill.disabled) return;
      const group = pill.closest('[data-byob-option]');
      group?.querySelectorAll('[data-byob-pill]').forEach((button) => {
        button.setAttribute('aria-pressed', button === pill ? 'true' : 'false');
      });
      syncCard(card, true);
      renderSummary();
      return;
    }

    if (target.closest('[data-byob-add]') && card instanceof HTMLElement) {
      setQty(card, 1);
      return;
    }

    if (target.closest('[data-byob-dec]') && card instanceof HTMLElement) {
      setQty(card, currentQty(card) - 1);
      return;
    }

    if (target.closest('[data-byob-inc]') && card instanceof HTMLElement) {
      setQty(card, currentQty(card) + 1);
      return;
    }

    if (target.closest('[data-byob-atc]')) {
      submit();
    }
  });

  renderSummary();

  /**
   * @param {HTMLElement} card
   */
  function entryFor(card) {
    return state.get(card.dataset.blockId || card.id);
  }

  /**
   * @param {HTMLElement} card
   * @param {Array<Record<string, any>>} variants
   */
  function ensureSelection(card, variants) {
    const groups = [...card.querySelectorAll('[data-byob-option]')];
    const hasSelection = groups.every((group) => group.querySelector('[aria-pressed="true"]'));
    if (hasSelection || !groups.length) return;

    const initial = variants.find((variant) => variant.available) || variants[0];
    groups.forEach((group, index) => {
      const value = initial?.options?.[index];
      group.querySelectorAll('[data-byob-pill]').forEach((pill) => {
        pill.setAttribute('aria-pressed', pill.dataset.value === value ? 'true' : 'false');
      });
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {Array<Record<string, any>>} variants
   */
  function selectedOptions(card, variants) {
    const groups = [...card.querySelectorAll('[data-byob-option]')];
    if (!groups.length) return variants[0]?.options || [];
    return groups.map((group) => group.querySelector('[aria-pressed="true"]')?.dataset.value || '');
  }

  /**
   * @param {HTMLElement} card
   * @param {Array<Record<string, any>>} variants
   */
  function resolveVariant(card, variants) {
    const groups = [...card.querySelectorAll('[data-byob-option]')];
    groups.forEach((group) => {
      const pressed = [...group.querySelectorAll('[aria-pressed="true"]')];
      pressed.slice(1).forEach((pill) => pill.setAttribute('aria-pressed', 'false'));
    });
    const selected = selectedOptions(card, variants);

    groups.forEach((group, index) => {
      group.querySelectorAll('[data-byob-pill]').forEach((pill) => {
        if (!(pill instanceof HTMLButtonElement)) return;
        const candidate = selected.slice();
        candidate[index] = pill.dataset.value || '';
        const available = variants.some(
          (variant) => variant.available && variant.options.every((option, optionIndex) => option === candidate[optionIndex])
        );
        pill.disabled = !available;
        pill.setAttribute('aria-disabled', available ? 'false' : 'true');
      });
    });

    return (
      variants.find((variant) => variant.options.every((option, index) => option === selected[index])) || null
    );
  }

  /**
   * @param {Record<string, any> | null} variant
   */
  function maxQuantity(variant) {
    if (!variant?.available) return 0;
    const tracked = Boolean(variant.inventory_management) && variant.inventory_policy !== 'continue';
    if (tracked && Number.isFinite(variant.inventory_quantity)) {
      return Math.max(0, variant.inventory_quantity);
    }
    return 99;
  }

  /**
   * @param {HTMLElement} card
   * @param {number} next
   */
  function currentQty(card) {
    const entry = entryFor(card);
    if (!entry) return 0;
    const variant = resolveVariant(card, entry.variants);
    return variant ? entry.lines.get(variant.id) || 0 : 0;
  }

  function setQty(card, next) {
    const entry = entryFor(card);
    if (!entry) return;
    const variant = resolveVariant(card, entry.variants);
    if (!variant) return;
    const qty = Math.max(0, Math.min(next, maxQuantity(variant)));
    if (qty === 0) entry.lines.delete(variant.id);
    else entry.lines.set(variant.id, qty);
    syncCard(card, false);
    renderSummary();
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} animateImage
   */
  function syncCard(card, animateImage) {
    const entry = entryFor(card);
    if (!entry) return;

    const variant = resolveVariant(card, entry.variants);
    const max = maxQuantity(variant);
    const qty = variant ? Math.min(entry.lines.get(variant.id) || 0, max) : 0;
    if (variant) {
      if (qty === 0) entry.lines.delete(variant.id);
      else if ((entry.lines.get(variant.id) || 0) !== qty) entry.lines.set(variant.id, qty);
    }

    const priceEl = card.querySelector('[data-byob-price]');
    const compareEl = card.querySelector('[data-byob-compare]');
    const imageEl = card.querySelector('[data-byob-image]');
    const addButton = card.querySelector('[data-byob-add]');
    const stepper = card.querySelector('[data-byob-stepper]');
    const qtyEl = card.querySelector('[data-byob-qty-value]');
    const incButton = card.querySelector('[data-byob-inc]');

    if (priceEl && variant) priceEl.textContent = formatMoney(variant.price);
    if (compareEl) {
      const showCompare = Boolean(variant && variant.compare_at_price > variant.price);
      compareEl.hidden = !showCompare;
      compareEl.textContent = showCompare ? formatMoney(variant.compare_at_price) : '';
    }

    if (imageEl instanceof HTMLImageElement && variant?.image) {
      if (animateImage && imageEl.getAttribute('src') !== variant.image) {
        imageEl.classList.remove('is-swapping');
        void imageEl.offsetWidth;
        imageEl.classList.add('is-swapping');
      }
      imageEl.src = variant.image;
      imageEl.alt = entry.title;
    }

    const inCart = qty > 0;
    if (addButton instanceof HTMLButtonElement) {
      addButton.hidden = inCart;
      addButton.disabled = !variant?.available;
      addButton.textContent = variant?.available ? 'ADD' : 'Sold out';
    }
    if (stepper instanceof HTMLElement) stepper.hidden = !inCart;
    if (qtyEl) qtyEl.textContent = String(qty);
    if (incButton instanceof HTMLButtonElement) incButton.disabled = qty >= max;
  }

  function selectedLines() {
    /** @type {Array<{entry: {lines: Map<number, number>, variants: Array<Record<string, any>>, card: HTMLElement, title: string}, variant: Record<string, any>, qty: number}>} */
    const lines = [];
    for (const entry of state.values()) {
      for (const [variantId, qty] of entry.lines) {
        if (!qty) continue;
        const variant = entry.variants.find((item) => item.id === variantId);
        if (!variant) continue;
        lines.push({ entry, variant, qty });
      }
    }
    return lines;
  }

  function totalQty() {
    return selectedLines().reduce((total, line) => total + line.qty, 0);
  }

  /**
   * @param {number} total
   */
  function activeTier(total) {
    const tier3Qty = Number(root.dataset.tier3Qty) || 3;
    const tier2Qty = Number(root.dataset.tier2Qty) || 2;
    const tier3Percent = Number(root.dataset.tier3Percent) || 0;
    const tier2Percent = Number(root.dataset.tier2Percent) || 0;

    if (total >= tier3Qty) {
      return { step: 3, percent: tier3Percent, code: root.dataset.tier3Code || '' };
    }
    if (total >= tier2Qty) {
      return { step: 2, percent: tier2Percent, code: root.dataset.tier2Code || '' };
    }
    if (total >= 1) return { step: 1, percent: 0, code: '' };
    return { step: 0, percent: 0, code: '' };
  }

  /**
   * @param {number} total
   */
  function progressScale(total) {
    const tier3Qty = Number(root.dataset.tier3Qty) || 3;
    const tier2Qty = Number(root.dataset.tier2Qty) || 2;
    if (total <= 0) return 0;
    if (total < tier2Qty) return 0.33;
    if (total < tier3Qty) return 0.66;
    return 1;
  }

  function renderSummary() {
    const total = totalQty();
    const tier = activeTier(total);
    const units = [];
    let mrp = 0;
    let catalog = 0;

    for (const line of selectedLines()) {
      const reference = line.variant.compare_at_price > line.variant.price ? line.variant.compare_at_price : line.variant.price;
      mrp += reference * line.qty;
      catalog += line.variant.price * line.qty;
      for (let index = 0; index < line.qty; index += 1) {
        units.push({
          image: line.variant.image || '',
          title: line.entry.title,
          key: `${line.variant.id}-${index}`,
        });
      }
    }

    const finalPrice = Math.round(catalog * (1 - tier.percent / 100));
    const savings = Math.max(0, mrp - finalPrice);
    const busy = root.dataset.byobBusy === 'true';

    if (countEl) {
      const noun = total === 1 ? 'Jar' : 'Jars';
      countEl.textContent = `${total} ${noun} Selected`;
    }

    if (fillEl instanceof HTMLElement) {
      fillEl.style.transform = `scaleX(${progressScale(total)})`;
    }

    root.querySelectorAll('[data-byob-step]').forEach((step) => {
      const level = Number(step.getAttribute('data-byob-step'));
      step.classList.toggle('is-active', tier.step === level);
      step.classList.toggle('is-reached', tier.step >= level && level > 0);
    });

    slots.forEach((slot, index) => {
      const unit = units[index];
      const image = slot.querySelector('img');
      if (!(image instanceof HTMLImageElement)) return;
      if (!unit) {
        slot.classList.remove('is-filled', 'is-entering');
        delete slot.dataset.key;
        delete slot.dataset.src;
        image.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
        return;
      }

      const wasFilled = slot.classList.contains('is-filled');
      const changed = slot.dataset.key !== unit.key;
      slot.dataset.key = unit.key;
      slot.dataset.src = unit.image;
      slot.classList.add('is-filled');
      if (unit.image) {
        image.src = unit.image;
        image.alt = unit.title;
        image.hidden = false;
      }
      if (changed && !wasFilled) {
        slot.classList.remove('is-entering');
        void slot.offsetWidth;
        slot.classList.add('is-entering');
      }
    });

    if (moreEl) {
      const extra = units.length - slots.length;
      moreEl.hidden = extra <= 0;
      moreEl.textContent = extra > 0 ? `+${extra} more` : '';
    }

    renderThumbs();

    if (mrpEl) {
      mrpEl.hidden = !(mrp > finalPrice && total > 0);
      mrpEl.textContent = formatMoney(mrp);
    }
    if (finalEl) finalEl.textContent = formatMoney(finalPrice);
    if (saveEl) {
      saveEl.hidden = savings <= 0;
      saveEl.textContent = savings > 0 ? `You Save ${formatMoney(savings)}` : '';
    }
    if (atcButton instanceof HTMLButtonElement) atcButton.disabled = total === 0 || busy;
  }

  function renderThumbs() {
    if (!thumbsEl) return;
    const selected = selectedLines();
    const visible = selected.slice(0, 4);
    const extra = selected.length - visible.length;
    const signature = `${visible.map((line) => line.variant.id).join(',')}:${extra}`;
    if (thumbsEl.dataset.signature === signature) return;
    thumbsEl.dataset.signature = signature;
    thumbsEl.replaceChildren();

    visible.forEach((line) => {
      if (line.variant.image) {
        const image = document.createElement('img');
        image.className = 'byob-bar__thumb';
        image.width = 36;
        image.height = 36;
        image.alt = line.entry.title;
        image.src = line.variant.image;
        thumbsEl.appendChild(image);
        return;
      }

      const fallback = document.createElement('span');
      fallback.className = 'byob-bar__thumb';
      fallback.setAttribute('role', 'img');
      fallback.setAttribute('aria-label', line.entry.title);
      thumbsEl.appendChild(fallback);
    });

    if (extra > 0) {
      const badge = document.createElement('span');
      badge.className = 'byob-bar__thumb byob-bar__thumb--more';
      badge.textContent = `+${extra}`;
      thumbsEl.appendChild(badge);
    }
  }

  function collectItems() {
    const bundleName = root.dataset.bundleName || 'Custom Ruokamill Bundle';
    /** @type {Map<number, number>} */
    const merged = new Map();

    for (const line of selectedLines()) {
      if (!line.variant.available) continue;
      merged.set(line.variant.id, (merged.get(line.variant.id) || 0) + line.qty);
    }

    return [...merged.entries()].map(([id, quantity]) => ({
      id,
      quantity,
      properties: { _Bundle: bundleName },
    }));
  }

  async function submit() {
    if (root.dataset.byobBusy === 'true') return;
    const items = collectItems();
    if (!items.length) return;

    root.dataset.byobBusy = 'true';
    if (atcButton instanceof HTMLButtonElement) {
      atcButton.classList.add('is-loading');
      atcButton.setAttribute('aria-busy', 'true');
    }
    clearError();
    renderSummary();

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);

      /** @type {Record<string, any>} */
      const payload = { items };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      const added = await postJson(cartUrl(root, 'cartAddUrl', '/cart/add.js'), payload);
      if (added.status) throw new Error(errorText(added));

      const discount = await applyTierDiscount(sectionIds, added.sections);
      const addedQty = items.reduce((sum, item) => sum + item.quantity, 0);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: root.dataset.sectionId || 'build-your-bundle',
            data: {
              source: 'product-form-component',
              itemCount: addedQty,
              sections: discount.sections,
            },
          },
        })
      );

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    } finally {
      root.dataset.byobBusy = 'false';
      if (atcButton instanceof HTMLButtonElement) {
        atcButton.classList.remove('is-loading');
        atcButton.removeAttribute('aria-busy');
      }
      renderSummary();
    }
  }

  /**
   * @param {string[]} sectionIds
   * @param {Record<string, string> | undefined} fallbackSections
   */
  async function applyTierDiscount(sectionIds, fallbackSections) {
    const tier2Code = root.dataset.tier2Code || '';
    const tier3Code = root.dataset.tier3Code || '';
    if (!tier2Code && !tier3Code) {
      return { ok: true, sections: fallbackSections };
    }

    const tier = activeTier(totalQty());
    const code = tier.percent > 0 ? tier.code : '';

    try {
      /** @type {Record<string, any>} */
      const payload = { discount: code };
      if (sectionIds.length) payload.sections = sectionIds;
      const updated = await postJson(cartUrl(root, 'cartUpdateUrl', '/cart/update.js'), payload);
      const failed =
        Boolean(code) &&
        Array.isArray(updated.discount_codes) &&
        updated.discount_codes.some((entry) => {
          return entry?.code?.toLowerCase() === code.toLowerCase() && entry.applicable === false;
        });

      if (failed) {
        showError(`Discount code ${code} could not be applied. The cart is charging the regular price.`);
        return { ok: false, sections: updated.sections || fallbackSections };
      }

      return { ok: true, sections: updated.sections || fallbackSections };
    } catch (error) {
      if (!code) return { ok: true, sections: fallbackSections };
      showError(
        error instanceof Error
          ? error.message
          : 'Your bundle is in the cart, but the discount could not be applied.'
      );
      return { ok: false, sections: fallbackSections };
    }
  }

  /**
   * @param {number} cents
   */
  function formatMoney(cents) {
    const amount = Number(cents) || 0;
    const format = root.dataset.moneyFormat || '₹{{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      const spec = MONEY_TOKENS[token] || MONEY_TOKENS.amount;
      const [precision, thousands, decimal] = spec;
      const rounded = precision === 0 ? Math.round(amount / 100) * 100 : amount;
      const fixed = (rounded / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    });
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  /**
   * @param {string} message
   */
  function showError(message) {
    if (!errorEl) return;
    errorEl.hidden = false;
    errorEl.textContent = message;
  }
}

/**
 * @param {string} url
 * @param {Record<string, any>} body
 */
async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    throw new Error(errorText(data));
  }

  return data;
}

/**
 * @param {Record<string, any>} data
 */
function errorText(data) {
  if (typeof data?.description === 'string' && data.description) return data.description;
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (data?.description && typeof data.description === 'object') {
    return Object.values(data.description).flat().join(', ');
  }
  return 'Something went wrong. Please try again.';
}

/**
 * @param {HTMLElement} root
 * @param {'cartAddUrl' | 'cartUpdateUrl'} key
 * @param {string} fallback
 */
function cartUrl(root, key, fallback) {
  const route = root.dataset[key] || window.Theme?.routes?.[key === 'cartAddUrl' ? 'cart_add_url' : 'cart_update_url'] || fallback;
  return route.endsWith('.js') ? route : `${route}.js`;
}

function boot() {
  document.querySelectorAll('[data-byob]').forEach((root) => initBundle(root));
}

if (!window.__byobBooted) {
  window.__byobBooted = true;
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-byob]');
    if (root) initBundle(root);
  });
}

boot();
